import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import getApiClient from "@/lib/api";
import { unwrap } from "@/lib/api/error";
import type { components } from "@/lib/api.gen";
type TotpStartResponse = components["schemas"]["TotpStartResponse"];
type RecoveryCodesResponse = components["schemas"]["RecoveryCodesResponse"];
type WebauthnRegisterFinishResponse = components["schemas"]["WebauthnRegisterFinishResponse"];

/**
 * webauthn-rs wraps its challenges as `{ publicKey: {...} }`, matching the
 * WebAuthn JSON wire format. `@simplewebauthn/browser` v13 takes the inner
 * options object as `optionsJSON`, so unwrap `publicKey` here.
 */
function optionsJSON<T>(challenge: unknown): T {
  const c = challenge as {
    publicKey?: T;
  };
  return (c.publicKey ?? challenge) as T;
}

// --- Settings: TOTP -----------------------------------------------------

export function useTotpStart() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      mutationFn: async () => unwrap(await apiClient.POST("/api/user/2fa/totp/start"))
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useMutation(t1);
}
export function useTotpConfirm(options) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async payload => unwrap(await apiClient.POST("/api/user/2fa/totp/confirm", {
      body: payload
    }));
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user-summary"]
        });
        options?.onSuccess?.();
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}
export function useTotpDisable(options) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async payload => {
      unwrap(await apiClient.POST("/api/user/2fa/totp/disable", {
        body: payload
      }));
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user-summary"]
        });
        options?.onSuccess?.();
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}

// --- Settings: WebAuthn -------------------------------------------------

export function useWebauthnCredentials() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["webauthn-credentials"],
      queryFn: async () => unwrap(await apiClient.GET("/api/user/2fa/webauthn"))
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useQuery(t1);
}

/**
 * Register a new security key: run the full WebAuthn ceremony (server start →
 * browser create → server finish). Returns any one-time recovery codes issued
 * when this enables 2FA for the first time.
 */
export function useRegisterWebauthn(options) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async name => {
      const challenge = unwrap(await apiClient.POST("/api/user/2fa/webauthn/register/start"));
      const credential = await startRegistration({
        optionsJSON: optionsJSON(challenge)
      });
      return unwrap(await apiClient.POST("/api/user/2fa/webauthn/register/finish", {
        body: {
          credential,
          name
        } as never
      }));
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["webauthn-credentials"]
        });
        queryClient.invalidateQueries({
          queryKey: ["user-summary"]
        });
        options?.onSuccess?.();
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}
export function useRenameWebauthn(options) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async t2 => {
      const {
        id,
        name
      } = t2;
      unwrap(await apiClient.PATCH("/api/user/2fa/webauthn/{id}", {
        params: {
          path: {
            id
          }
        },
        body: {
          name
        }
      }));
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["webauthn-credentials"]
        });
        options?.onSuccess?.();
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}
export function useDeleteWebauthn(options) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async t2 => {
      const {
        id,
        password
      } = t2;
      unwrap(await apiClient.DELETE("/api/user/2fa/webauthn/{id}", {
        params: {
          path: {
            id
          }
        },
        body: {
          password
        }
      }));
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["webauthn-credentials"]
        });
        queryClient.invalidateQueries({
          queryKey: ["user-summary"]
        });
        options?.onSuccess?.();
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}

// --- Settings: recovery codes ------------------------------------------

export function useRegenerateRecoveryCodes(options) {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiClient();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const apiClient = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async payload => unwrap(await apiClient.POST("/api/user/2fa/recovery/regenerate", {
      body: payload
    }));
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== queryClient) {
    t2 = {
      mutationFn: t1,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user-summary"]
        });
        options?.onSuccess?.();
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return useMutation(t2);
}

// --- Login-time challenge helpers --------------------------------------
// Plain async functions (the user is not yet authenticated). Each throws an
// ApiError on failure and resolves once the login is finalized.

export async function loginVerifyTotp(code: string): Promise<void> {
  const apiClient = getApiClient();
  unwrap(await apiClient.POST("/api/user/login/2fa/totp", {
    body: {
      code
    }
  }));
}
export async function loginVerifyRecovery(code: string): Promise<void> {
  const apiClient = getApiClient();
  unwrap(await apiClient.POST("/api/user/login/2fa/recovery", {
    body: {
      code
    }
  }));
}

/** Start the e-mail second factor; returns the confirmation passphrase indices. */
export async function loginStartEmail2fa(): Promise<number[]> {
  const apiClient = getApiClient();
  const data = unwrap(await apiClient.POST("/api/user/login/2fa/email/start"));
  return data.passphrase;
}
export async function loginVerifyEmail2fa(): Promise<void> {
  const apiClient = getApiClient();
  unwrap(await apiClient.POST("/api/user/login/2fa/email/verify"));
}

/** Run the WebAuthn authentication ceremony to complete the second factor. */
export async function loginVerifyWebauthn(): Promise<void> {
  const apiClient = getApiClient();
  const challenge = unwrap(await apiClient.POST("/api/user/login/2fa/webauthn/start"));
  const credential = await startAuthentication({
    optionsJSON: optionsJSON(challenge)
  });
  unwrap(await apiClient.POST("/api/user/login/2fa/webauthn/finish", {
    body: {
      credential
    } as never
  }));
}